import styles from './styles.module.css';
import { useNavigate } from 'react-router-dom';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../../components/header/Header';
import { Form, Input } from 'antd';
import { useFujiAuth } from '../../../auth/useFujiAuth';
import { showNotificationDialog } from '../../../utils';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import ButtonNetwork from '../../../utils/network-connect/ButtonNetwork';
import { EMAIL_REGEX_PATTERN } from '../../../utils/Constants';

/**
 * 図102-1 新規登録画面
 */
const RegisterEmailScreen = () => {
    const { t } = useTranslation();
    const { signUp } = useFujiAuth();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);

    const onFinish = ({ email, password }: { email: string; password: string }) => {
        setIsLoading(true);
        // 小文字に変換する
        const emailLower = email.trim().toLowerCase();
        signUp(
            emailLower,
            password,
            ({ isSuccessed, error }: { isSuccessed: boolean; error: { code: string; message: string[] } }) => {
                setIsLoading(false);
                if (isSuccessed) {
                    navigate('/auth/confirm', { state: { email: emailLower } });
                } else if (error.code === 'UsernameExistsException') {
                    // 1-E1 メールアドレスが登録済み
                    showNotificationDialog(
                        'error',
                        'register_failed',
                        t('notification.title'),
                        t('error-message.register.already-registered'),
                    );
                } else if (error.code === 'CodeDeliveryFailureException') {
                    // 1-E3 確認コード送信に失敗
                    showNotificationDialog(
                        'error',
                        'register_failed',
                        t('notification.title'),
                        t('error-message.register.send-code-failed'),
                    );
                } else if (error.message.includes('Network error')) {
                    showNotificationDialog('error', '', t('notification.title'), t('error-message.server-error'));
                } else {
                    // 1-E5 ユーザー登録に失敗
                    showNotificationDialog(
                        'error',
                        'register_failed',
                        t('notification.title'),
                        t('error-message.register.register-failed'),
                    );
                }
            },
        );
    };
    const onFinishFailed = () => {};

    return (
        <React.Fragment>
            {isLoading && <CenterLoading overFooter={true} />}

            <Header title={t('header.register')} redirect="/auth" />
            <div className={`content-footer-container scrollable ${styles.registerContainer}`}>
                <div className={styles.registerFormContainer}>
                    <Form
                        className={styles.registerForm}
                        initialValues={{
                            remember: true,
                        }}
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        autoComplete="off"
                    >
                        <Form.Item
                            name="email"
                            rules={[
                                {
                                    pattern: EMAIL_REGEX_PATTERN,
                                    message: t('validate.correct-email') ?? '',
                                },
                                {
                                    required: true,
                                    message: t('validate.email') ?? '',
                                },
                            ]}
                        >
                            <Input
                                style={{ borderRadius: 4, height: 50 }}
                                placeholder={t('form.email') ?? ''}
                                size="large"
                                allowClear
                            />
                        </Form.Item>
                        <div className={styles.mailNote}>{t('change-mail.note')}</div>
                        <Form.Item
                            name="password"
                            rules={[
                                {
                                    min: 8,
                                    message: t('validate.password-8ormore') ?? '',
                                },
                                {
                                    pattern:
                                        // eslint-disable-next-line no-useless-escape
                                        /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[a-zA-Z0-9=+^$*.\[\]{}()?@#%&/\\,><':;|_~"!`-]/,
                                    message: t('validate.capital-number') ?? '',
                                },
                                {
                                    required: true,
                                    message: t('validate.password') ?? '',
                                },
                            ]}
                        >
                            <Input.Password
                                style={{ borderRadius: 4, height: 50 }}
                                placeholder={t('form.password') ?? ''}
                                size="large"
                                type="password"
                                autoComplete="password"
                                allowClear
                                maxLength={30}
                            />
                        </Form.Item>

                        <Form.Item
                            name="confirm"
                            dependencies={['password']}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t('validate.password-not-match') ?? ''));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
                                style={{ borderRadius: 4, height: 50 }}
                                placeholder={t('form.confirm-password') ?? ''}
                                size="large"
                                autoComplete="password"
                                allowClear
                                maxLength={30}
                            />
                        </Form.Item>
                        <p style={{ fontSize: 12.5, marginBottom: 20 }}>{t('password.requirement')}</p>
                        <Form.Item>
                            <ButtonNetwork title={t('button.register')} type="submit" />
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </React.Fragment>
    );
};

export default RegisterEmailScreen;
